import { useMemo } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type { PostInternalActionExecutionLogsData } from '~/generated/client'
import {
  getInternalActionExecutionFields,
  postInternalActionExecutionLogs,
} from '~/generated/client'
import { getDateFromTimeRange } from '~/utils/dateUtils'

// Interface representing a single log entry with app, connection, session and execution details
interface Log {
  id: string
  name: string
  app: {
    id: string
    name: string
    icon: string
  }
  connection: {
    id: string
    entity: string
  }
  session: {
    id: string
    entity: string
    source: string
    runtime: string
  }
  executionTime: string
  status: string
  responseMessage: string
}

// Response structure for paginated logs API
export interface LogsResponse {
  items: Log[]
  pagination: {
    cursor: string
    pageSize: number
  }
}

export const useLogsFilters = () => {
  return {
    status: {
      type: 'select',
      options: ['success', 'failed'],
    },
    appName: {
      type: 'text',
      operations: ['eq', 'neq', 'contains', 'not_contains'],
      search: true,
    },
    triggerId: {
      type: 'text',
      operations: ['eq', 'neq', 'contains', 'not_contains'],
      search: true,
    },
  }
}

/**
 * Custom hook for fetching and managing request logs with infinite scroll
 * Uses react-query for data fetching, caching and pagination
 * @returns Object containing logs data and utility functions for pagination
 */
export const useActionLogs = ({
  filter: { searchParams, pageSize, interval, caseSensitive },
}: {
  filter: {
    searchParams: NonNullable<
      PostInternalActionExecutionLogsData['requestBody']
    >['search_params']
    pageSize: number
    interval?: string | undefined
    caseSensitive?: boolean
  }
}) => {
  const { data: fields } = useQuery({
    queryKey: ['actionFields'],
    queryFn: () => getInternalActionExecutionFields(),
  })

  const {
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    data,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['logs', searchParams, interval, caseSensitive],
    queryFn: ({ pageParam = null }) =>
      postInternalActionExecutionLogs({
        requestBody: {
          cursor: pageParam,
          limit: pageSize,
          case_sensitive: caseSensitive,
          search_params: searchParams,
          from: interval ? getDateFromTimeRange(interval).getTime() : undefined,
        },
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })

  const logs = useMemo(
    () => data?.pages?.flatMap((page) => page?.data) ?? [],
    [data?.pages],
  )

  return {
    logs,
    fields,
    hasNextPage,
    fetchNextPage,
    refetch,
    isLoading,
    isFetchingNextPage,
    isError,
    isFetching,
  }
}
